import { useState, type FormEvent } from "react";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahhj3jp14a1h3mr6n40";

export default function SubscribeSection() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honey = String(formData.get("website_alt") ?? "").trim();
    if (honey) {
      setStatus("success");
      setSuccessMessage("订阅成功，更新后会第一时间通知你");
      return;
    }
    formData.delete("website_alt");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => payload.append(key, String(value)));

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const code = parsed?.code;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");

      if (response.ok && code === "OK" && !isSpam) {
        setStatus("success");
        setSuccessMessage(serverMsg || "订阅成功，更新后会第一时间通知你");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试");
    }
  };

  return (
    <section id="subscribe" className="bg-accent-100 py-14 md:py-20">
      <div className="mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 items-center gap-10 rounded-lg border border-accent-300 bg-background-50 p-6 md:p-10 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-2 font-label text-xs font-medium tracking-[0.2em] text-secondary-700">
              <i className="ri-notification-3-line w-4 h-4 flex items-center justify-center"></i>
              更新通知
            </span>
            <h2 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-3xl">
              留下邮箱，新合集更新时喊你一声
            </h2>
            <p className="mt-4 max-w-lg text-sm leading-relaxed text-foreground-600">
              黑料bu 每周固定更新主题合集与归档资料。订阅后，新增的
              <strong className="font-semibold text-foreground-800">黑料合集</strong>与
              <strong className="font-semibold text-foreground-800">黑料整理</strong>批次会通过邮件提醒你，
              不用天天回来看有没有新内容。
            </p>
            <ul className="mt-6 space-y-2.5">
              {["按关注的主题定向提醒", "只推更新，不发广告", "随时可取消订阅"].map((item) => (
                <li key={item} className="flex items-center gap-2.5 text-sm text-foreground-700">
                  <i className="ri-checkbox-circle-line w-4 h-4 flex items-center justify-center text-secondary-500"></i>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-6">
            <form
              id="subscribe-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6"
            >
              <div className="flex flex-col gap-4">
                <label className="flex flex-col gap-2">
                  <span className="font-label text-sm font-medium text-foreground-800">邮箱地址</span>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="h-11 rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </label>

                <label className="flex flex-col gap-2">
                  <span className="font-label text-sm font-medium text-foreground-800">想关注的主题</span>
                  <select
                    name="topic"
                    required
                    defaultValue=""
                    className="h-11 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  >
                    <option value="" disabled>
                      请选择一个主题
                    </option>
                    <option value="娱乐观察">娱乐观察</option>
                    <option value="网络热点">网络热点</option>
                    <option value="商业动态">商业动态</option>
                    <option value="科技圈">科技圈</option>
                    <option value="体坛风云">体坛风云</option>
                    <option value="全部主题">全部主题更新</option>
                  </select>
                </label>

                <input
                  type="text"
                  name="website_alt"
                  className="contact_alt-field"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                  {status === "submitting" ? "提交中…" : "订阅更新通知"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-2.5 font-label text-xs text-secondary-900">
                    <i className="ri-checkbox-circle-fill w-4 h-4 flex items-center justify-center"></i>
                    {successMessage}
                  </p>
                )}
                {status === "error" && formError && (
                  <p className="flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 font-label text-xs text-primary-800">
                    <i className="ri-error-warning-line w-4 h-4 flex items-center justify-center"></i>
                    {formError}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}